import { DiagnosticReport } from "@medplum/fhirtypes";
import { parseSearchQuery } from "../../../search";

export type MatchInfo = {
  text: string; // The matching text
  position: number; // Character position in full text
  snippet: string; // ~50 chars before/after for context
};

export type NoteMatchData = {
  count: number;
  snippets: MatchInfo[];
  fullNoteText: string; // For filtering (not displayed)
};

/**
 * Extracts and decodes notes from DiagnosticReport presentedForm field.
 * Returns an array of cleaned note strings.
 */
export function extractNotesFromDiagnosticReport(report: DiagnosticReport): string[] {
  const presentedForms = report.presentedForm || [];
  return presentedForms
    .map(form => {
      if (!form.data) return "";
      try {
        const decoded = atob(form.data);
        return cleanUpNote(decoded);
      } catch (error) {
        console.error("Failed to decode presentedForm data:", error);
        return "";
      }
    })
    .filter(note => note.trim().length > 0);
}

/**
 * Cleans up note text by removing unwanted XML tags and artifacts.
 * Based on logic from report-content.tsx
 */
function cleanUpNote(note: string): string {
  return note
    .trim()
    .replace(/<ID>.*?<\/ID>/g, "")
    .replace(/<styleCode>.*?<\/styleCode>/g, "")
    .replace(/<colspan>.*?<\/colspan>/g, "")
    .replace(/<script>.*?<\/script>/g, "")
    .replace(/<content>/g, "")
    .replace(/<\/content>/g, "")
    .replace(/<content\/>/g, "")
    .replace(/<root>/g, "")
    .replace(/<\/root>/g, "")
    .replace(/&lt;/g, "")
    .replace(/&gt;/g, "");
}

/**
 * Finds all matches of a query string within text and returns match information.
 * Case-insensitive search.
 * Supports both exact phrase matching and individual word matching (to align with FlexSearch).
 * Respects quoted phrases to keep multi-word terms together.
 */
export function findMatchesInText(text: string, query: string): MatchInfo[] {
  if (!query.trim() || !text) {
    return [];
  }

  const matches: MatchInfo[] = [];
  const lowerText = text.toLowerCase();

  // Parse query respecting quoted phrases
  const terms = parseSearchQuery(query);

  // Search for each term (which could be a single word or a phrase)
  for (const term of terms) {
    const lowerTerm = term.toLowerCase();
    let position = 0;

    while ((position = lowerText.indexOf(lowerTerm, position)) !== -1) {
      const matchText = text.substring(position, position + lowerTerm.length);
      const snippet = extractSnippet(text, position, 100);

      matches.push({
        text: matchText,
        position,
        snippet,
      });

      position += lowerTerm.length;
    }
  }

  // Sort by position and remove duplicates
  return matches
    .sort((a, b) => a.position - b.position)
    .filter((match, index, self) => {
      const prevMatch = self[index - 1];
      return index === 0 || !prevMatch || match.position !== prevMatch.position;
    });
}

/**
 * Extracts a snippet of text around a match position with context.
 * @param text Full text
 * @param matchPosition Position of the match in the text
 * @param contextChars Number of characters to show before/after the match
 */
export function extractSnippet(text: string, matchPosition: number, contextChars: number): string {
  const start = Math.max(0, matchPosition - contextChars);
  const end = Math.min(text.length, matchPosition + contextChars);

  let snippet = text.substring(start, end);

  // Add ellipsis if we're not at the start/end
  if (start > 0) {
    snippet = "..." + snippet;
  }
  if (end < text.length) {
    snippet = snippet + "...";
  }

  return snippet.trim();
}

/**
 * Creates NoteMatchData by searching for matches in note text.
 * Limits results to maxSnippets for performance.
 */
export function createNoteMatchData(
  noteText: string,
  query: string,
  maxSnippets = 5
): NoteMatchData | undefined {
  if (!query.trim() || !noteText) {
    return undefined;
  }

  const allMatches = findMatchesInText(noteText, query);

  if (allMatches.length === 0) {
    return undefined;
  }

  return {
    count: allMatches.length,
    snippets: allMatches.slice(0, maxSnippets),
    fullNoteText: noteText,
  };
}
